import { useEffect, useRef, useState } from 'react'
import SignatureCanvas from 'react-signature-canvas'
import { useSignatureCanvasSize } from '../../hooks/useSignatureCanvasSize'
import { useUploadFile } from '../../hooks/useUploadFile'
import { showToast } from '../../utils/toast'
import {
  dataUrlToFile,
  fileFromSignatureSrc,
  isSignatureImageSrc,
  type LabResultSignatureValue
} from './labTemplateFooter'

export type { LabResultSignatureValue }

type LabResultSignaturePadProps = {
  label: string
  onChange: (value: LabResultSignatureValue | null) => void
  value: LabResultSignatureValue | null
  /** Saved blob/data-URL from patient or staff profile */
  profileSignature?: string | null
  onSaveToProfile?: (signatureBlob: string) => Promise<void> | void
  onRemoveProfileSignature?: () => Promise<void> | void
  isUpdatingProfile?: boolean
  /**
   * When true (default), uploads the image and sets `signedId` from `/uploads`.
   * When false, only commits the blob as `previewUrl` (and uses it as `signedId` placeholder).
   */
  uploadOnSave?: boolean
}

const LabResultSignaturePad = ({
  label,
  onChange,
  value,
  profileSignature,
  onSaveToProfile,
  onRemoveProfileSignature,
  isUpdatingProfile = false,
  uploadOnSave = true
}: LabResultSignaturePadProps) => {
  const sigCanvas = useRef<SignatureCanvas>(null)
  const canvasBoxRef = useRef<HTMLDivElement>(null)
  const canvasSize = useSignatureCanvasSize(canvasBoxRef)
  const { isUploading, uploadFile } = useUploadFile()
  const [saveForNextTime, setSaveForNextTime] = useState(true)
  const [drawingNew, setDrawingNew] = useState(false)

  const profilePreview = isSignatureImageSrc(profileSignature) ? profileSignature : null
  const hasProfileSignature = !!profileSignature
  const hasCommittedValue = !!value?.previewUrl || (!!uploadOnSave && !!value?.signedId)
  const usingSavedProfile = !drawingNew && !hasCommittedValue && !!profilePreview
  const showCanvas = drawingNew || (!hasCommittedValue && !profilePreview)
  const previewSrc = value?.previewUrl || (!showCanvas ? profilePreview : null)

  useEffect(() => {
    if (hasProfileSignature) setSaveForNextTime(true)
  }, [hasProfileSignature])

  const clearSignature = () => {
    sigCanvas.current?.clear()
    onChange(null)
    setDrawingNew(true)
  }

  const saveSignature = async () => {
    try {
      let previewUrl = ''

      if (showCanvas) {
        if (sigCanvas.current?.isEmpty()) {
          showToast('Please sign before saving.')
          return
        }
        const dataUrl = sigCanvas.current?.getCanvas().toDataURL('image/png')
        if (!dataUrl) return
        previewUrl = dataUrl
      } else if (value?.previewUrl) {
        previewUrl = value.previewUrl
      } else if (profilePreview) {
        previewUrl = profilePreview
      } else {
        showToast('Please sign before saving.')
        return
      }

      if (uploadOnSave) {
        const file = previewUrl.startsWith('data:') ? dataUrlToFile(previewUrl) : await fileFromSignatureSrc(previewUrl)
        const { signed_id } = await uploadFile(file)
        onChange({ previewUrl, signedId: signed_id })
      } else {
        onChange({ previewUrl, signedId: previewUrl })
      }

      if (saveForNextTime && onSaveToProfile) await onSaveToProfile(previewUrl)
      showToast('Signature saved')
      setDrawingNew(false)
    } catch {
      showToast(uploadOnSave ? 'Failed to upload signature' : 'Failed to save signature')
    }
  }

  const handleRemoveSaved = async () => {
    if (!onRemoveProfileSignature) return
    try {
      await onRemoveProfileSignature()
      if (!hasCommittedValue) setDrawingNew(true)
      showToast('Saved signature removed')
    } catch {
      showToast('Failed to remove saved signature')
    }
  }

  const busy = isUploading || isUpdatingProfile

  return (
    <div className="min-w-0 w-full flex-1">
      <p className="font-medium text-[#111]">{label}</p>

      {usingSavedProfile ? (
        <p className="mt-1 text-xs text-[#6B7280]">
          Using your saved signature
          <span className="mx-1">•</span>
          <button
            className="font-medium text-[#1565C0] hover:underline"
            disabled={busy}
            onClick={() => setDrawingNew(true)}
            type="button"
          >
            Draw new
          </button>
        </p>
      ) : null}

      {previewSrc && !showCanvas ? (
        <div className="mt-3 flex h-24 max-w-lg items-center justify-center overflow-hidden rounded border border-dashed border-[#ccc] bg-[#fafafa]">
          <img
            alt="Signature"
            className={`max-h-20 max-w-full object-contain ${usingSavedProfile ? 'opacity-50' : ''}`}
            src={previewSrc}
          />
        </div>
      ) : value?.signedId && !value.previewUrl && !showCanvas ? (
        <div className="mt-3 flex h-24 max-w-lg items-center justify-center overflow-hidden rounded border border-dashed border-[#ccc] bg-[#fafafa]">
          <p className="text-xs text-[#6B7280]">Signature ready</p>
        </div>
      ) : (
        <div
          className="mt-3 h-24 max-w-lg overflow-hidden rounded border border-dashed border-[#ccc] bg-[#fafafa]"
          ref={canvasBoxRef}
        >
          {canvasSize.width > 0 ? (
            <SignatureCanvas
              canvasProps={{
                width: canvasSize.width,
                height: canvasSize.height,
                className: 'block cursor-crosshair'
              }}
              penColor="black"
              ref={sigCanvas}
            />
          ) : null}
        </div>
      )}

      <div className="mt-2 flex gap-4">
        <button
          className="text-xs font-medium text-[#1565C0] hover:underline disabled:opacity-50"
          disabled={busy}
          onClick={clearSignature}
          type="button"
        >
          Clear
        </button>
        <button
          className="text-xs font-medium text-[#2E7D32] hover:underline disabled:opacity-50"
          disabled={busy}
          onClick={() => void saveSignature()}
          type="button"
        >
          {isUploading ? 'Uploading…' : 'Save'}
        </button>
      </div>

      {(onSaveToProfile || hasProfileSignature) && (
        <div className="mt-3 flex max-w-lg flex-wrap items-center justify-between gap-3">
          {onSaveToProfile ? (
            <label className="flex cursor-pointer items-center gap-2 text-xs text-[#374151]">
              <input
                checked={saveForNextTime}
                className="h-4 w-4 rounded border-[#D1D5DB] text-[#1565C0] focus:ring-[#1565C0]"
                disabled={busy}
                onChange={e => setSaveForNextTime(e.target.checked)}
                type="checkbox"
              />
              Save for next time
            </label>
          ) : (
            <span />
          )}

          {hasProfileSignature && onRemoveProfileSignature ? (
            <button
              className="text-xs font-medium text-[#C62828] hover:underline disabled:opacity-50"
              disabled={busy}
              onClick={() => void handleRemoveSaved()}
              type="button"
            >
              {isUpdatingProfile ? 'Removing…' : 'Remove saved signature'}
            </button>
          ) : null}
        </div>
      )}
    </div>
  )
}

export default LabResultSignaturePad
